import axiosNoAuthInstance from "../../axiosApiNoAuth";
import { useState, useEffect } from "react";
import swal from "sweetalert";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Box, CircularProgress } from "@mui/material";
import { useAtom } from "jotai";
import {
  firmSizeRangeAtom,
  selectedFiltersAtom,
  selectedPlaceAtom,
  selectedSpatialLayerAtom,
  selectedSpatialValueAtom,
  smallBusinessJobsGrowthDataAtom,
  smallBusinessJobsGrowthGroupedAtom,
  spatialParameterAtom,
} from "../../../shared/state/atoms";

// Small Business / Jobs Growth — mirrors TotalJobsGrowth.js but calls the
// small-business-specific endpoint with firm-size range filtering.
function SmallBusinessJobsGrowth() {
  const [selectedPlace] = useAtom(selectedPlaceAtom);
  const [selectedFilters] = useAtom(selectedFiltersAtom);
  const [data, setData] = useAtom(smallBusinessJobsGrowthDataAtom);
  const [groupedMunicipalities, setGroupedMunicipalities] = useAtom(smallBusinessJobsGrowthGroupedAtom);
  const [selectedSpatialValue] = useAtom(selectedSpatialValueAtom);
  const [spatialParameter] = useAtom(spatialParameterAtom);
  const [selectedSpatialLayer] = useAtom(selectedSpatialLayerAtom);
  const [firmSizeRange] = useAtom(firmSizeRangeAtom);

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // 1-handle slider: small segment is [0, handle).
  const handles = firmSizeRange?.smallBusinessJobsGrowth?.handles ?? [50];
  const inverted = firmSizeRange?.smallBusinessJobsGrowth?.inverted ?? false;
  const firmsize_fte_lower = inverted ? handles[0] : 0;
  const firmsize_fte_upper = inverted ? 2000000 : handles[0];
  const isEmptyRange = firmsize_fte_lower === firmsize_fte_upper;

  useEffect(() => {
    // Empty range — clear data and show message (emulates SmallBusinessMapIntensity)
    if (isEmptyRange) {
      setData([]);
      setGroupedMunicipalities([]);
      setIsLoading(false);
      setErrorMessage("No firm size range selected. Adjust the slider to select a range.");
      return;
    }

    async function loadData() {
      setIsLoading(true);
      setErrorMessage(null);
      try {
        let response;
        if (selectedFilters && selectedFilters.baseYear) {
          if (spatialParameter !== "error") {
            response = await axiosNoAuthInstance.get(
              `/api/dashboard-small-business-jobs-growth/?${spatialParameter}=${selectedSpatialValue}` +
              `&metro=${selectedFilters?.isMetroDisplayed ? "1" : "0"}` +
              `&national=${selectedFilters?.isNationalDisplayed ? "1" : "0"}` +
              `&same_province=${selectedFilters?.isProvinceDisplayed ? "1" : "0"}` +
              `&similar_size=${selectedFilters?.isSimilarSizeDisplayed ? "1" : "0"}` +
              `&base_year=${selectedFilters?.baseYear}` +
              `&firmsize_fte_lower=${firmsize_fte_lower}` +
              `&firmsize_fte_upper=${firmsize_fte_upper}`
            );
          }

          const parsedData = response?.data
            .filter((row) => row.place_name && row.place_name.trim() !== "")
            .map((row) => ({
              ...row,
              value: parseFloat(row.value.toFixed(2)),
              abs_value: row.abs_value != null ? parseFloat(row.abs_value.toFixed(2)) : 0,
            }));

          const groupedData = parsedData.reduce((group, product) => {
            const { year } = product;
            group[year] = group[year] ?? [];
            group[year].push(product);
            return group;
          }, {});

          const mappedData = Object.keys(groupedData).map((year) => {
            let yearObject = groupedData[year].reduce((acc, cur) => {
              acc[cur.place_name] = cur.value;
              acc[`${cur.place_name}_abs`] = cur.abs_value;
              return acc;
            }, {});
            return { year, ...yearObject };
          });

          const groupedMunics = parsedData.reduce((acc, cur) => {
            const { place_name, hex_colour } = cur;
            const existingObj = acc.find((obj) => obj.place_name === place_name);
            if (existingObj) {
              existingObj.data.push({ year: cur.year, value: cur.value, abs_value: cur.abs_value });
            } else {
              acc.push({
                place_name,
                hex_colour,
                data: [{ year: cur.year, value: cur.value, abs_value: cur.abs_value }],
              });
            }
            return acc;
          }, []);

          if (response) {
            setData(mappedData);
            setErrorMessage(null);
          } else {
            setData([]);
            setErrorMessage(`No Data available for ${selectedFilters?.baseYear}`);
          }

          setGroupedMunicipalities(groupedMunics);
          setIsLoading(false);
        }
      } catch (error) {
        if (error?.response?.status === 404 || error?.response == null) {
          setIsLoading(false);
          setErrorMessage("No Data");
        } else {
          swal("Failed", `${error}`, "error", { timer: 4000, buttons: false });
        }
      }
    }
    if (spatialParameter && selectedSpatialValue && selectedFilters && !isLoading) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spatialParameter, selectedSpatialValue, selectedFilters, firmsize_fte_lower, firmsize_fte_upper]);

  const CustomTooltip = ({ active, payload, label }) => {
    payload?.sort((a, b) => b.value - a.value);

    const calculatePlaceWidth = () => {
      if (!payload || payload.length === 0) return 120;
      const maxLength = Math.max(...payload.map((item) => item.name.length));
      return Math.max(120, maxLength * 8 + 40);
    };

    const placeWidth = calculatePlaceWidth();

    if (active && payload && payload.length) {
      return (
        <div className="p-2 bg-white" key={label}>
          <p className="m-0">{label}</p>
          <div style={{ display: "flex", color: "#999999", fontSize: "0.67em", margin: 0 }}>
            <div style={{ width: `${placeWidth}px`, paddingRight: "16px" }}>Place</div>
            <div style={{ width: "100px", paddingRight: "16px", whiteSpace: "nowrap" }}>Indexed Value</div>
            <div style={{ width: "80px" }}>FTE Value</div>
          </div>
          {payload.map((munic) => {
            const absKey = `${munic.name}_abs`;
            const absValue = munic.payload[absKey] != null ? munic.payload[absKey] : 0;
            return (
              <div key={munic.name} className={payload.length >= 12 ? "text-xs" : ""} style={{ display: "flex", color: munic.color, margin: 0 }}>
                <div style={{ width: `${placeWidth}px`, paddingRight: "16px" }}>{munic.name}</div>
                <div style={{ width: "100px", paddingRight: "16px" }}>{munic.value}</div>
                <div style={{ width: "80px" }}>{Math.round(absValue).toLocaleString()}</div>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="flex">
      <div className="w-full">
        <div className="text-lg px-10 pt-5 font-sans font-bold">
          {selectedSpatialLayer?.name} / {selectedPlace?.name} / <span style={{ color: "#1976d2" }}>Small Business</span>
        </div>
        <div className="text-3xl font-bold px-10 mb-10 font-roboto">Jobs Growth</div>

        {!errorMessage ? (
          <div>
            {isLoading && (
              <div className="absolute left-0 top-0 w-full h-full opacity-30 bg-ccre-gray-900 z-20">
                <Box sx={{ textAlign: "center", position: "relative", top: "40%" }}>
                  <CircularProgress color="success" size="3rem" />
                </Box>
              </div>
            )}
            <ResponsiveContainer width="90%" height={500}>
              <LineChart
                data={data}
                margin={{ top: 10, right: 50, left: 100, bottom: 26 }}
              >
                <XAxis dataKey="year" label={{ value: "Year", dy: 32 }} />
                <YAxis domain={["auto", "auto"]} label={{ value: "Indexed Small Business Jobs", angle: -90, dx: -50 }} />
                <CartesianGrid strokeDasharray="3" vertical={false} />
                {!isLoading && groupedMunicipalities.map((munic) => (
                  <Line key={munic.place_name} type="monotone" dataKey={munic.place_name} stroke={munic.hex_colour} />
                ))}
                <Tooltip content={<CustomTooltip />} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="p-20 flex justify-center">{errorMessage}</div>
        )}
      </div>
    </div>
  );
}

export default SmallBusinessJobsGrowth;
